import { useAsyncData } from '#imports'

import { getAsyncDataKey } from '@backmarket/nuxt-module-http-v2/getAsyncDataKey'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'

import type { WeekendDeliveriesConfiguration } from '~/api/shipping/carrier.types'
import { useSellerInfoStore } from '~/scopes/auth/stores/sellerInfoStore'

import { WEEKEND_DELIVERIES_CONFIGURATION_CACHE_KEY } from '../ManageWeekendDelivery.constants'
import translations from '../ManageWeekendDelivery.translations'
import type { WeekendDeliveryConfiguration } from '../ManageWeekendDelivery.types'

export async function useGetCarrierWeekendDeliveriesConfiguration() {
  const i18n = useI18n()
  const $httpFetch = useHttpFetch()
  const logger = useLogger()
  const { openErrorToast } = useTheToast()
  const { publicId: sellerPublicId } = useSellerInfoStore()

  return useAsyncData(
    getAsyncDataKey(WEEKEND_DELIVERIES_CONFIGURATION_CACHE_KEY),
    async () => {
      if (!sellerPublicId) {
        const emptyConfiguration: Array<WeekendDeliveriesConfiguration> = []

        return emptyConfiguration
      }
      try {
        return $httpFetch(
          '/bm/shipping/carriers/v1/sellers/:sellerPublicId/carrier-weekend-deliveries/configuration',
          {
            method: 'GET',
            pathParams: { sellerPublicId },
          },
        )
      } catch (err) {
        logger.error(
          'Cannot retrieve carrier weekend deliveries configuration',
          {
            error: err as Error,
            sellerPublicId,
            owners: ['bot-squad-shipping-front'],
          },
        )
        openErrorToast({
          title: i18n(
            translations.manageWeekendDeliveryGetConfigurationErrorTitle,
          ),
          content: i18n(
            translations.manageWeekendDeliveryGetConfigurationErrorContent,
          ),
        })
        throw err
      }
    },
    {
      server: false,
      transform: (carrierWeekendDeliveryConfigurations) => {
        const flattenedWeekendDeliveryConfigurations: Array<WeekendDeliveryConfiguration> =
          carrierWeekendDeliveryConfigurations
            .flatMap(({ carrierCodename, carrierName, configurations }) =>
              configurations.map((configuration) => ({
                carrierCodename,
                carrierName,
                ...configuration,
              })),
            )
            .flatMap(
              ({
                id,
                countryCode,
                carrierCodename,
                carrierName,
                weekendDeliveries,
              }) =>
                weekendDeliveries.map((weekendDelivery) => ({
                  carrierCodename,
                  carrierName,
                  id,
                  countryCode,
                  ...weekendDelivery,
                })),
            )
            .filter(
              (
                flattenedWeekDeliveryConfiguration,
              ): flattenedWeekDeliveryConfiguration is WeekendDeliveryConfiguration =>
                flattenedWeekDeliveryConfiguration.day === 'SATURDAY' ||
                flattenedWeekDeliveryConfiguration.day === 'SUNDAY',
            )

        const totalAvailableMarkets = new Set(
          flattenedWeekendDeliveryConfigurations.map(
            ({ countryCode }) => countryCode,
          ),
        ).size
        const isWeekendDeliveryConfigured =
          flattenedWeekendDeliveryConfigurations
            .map(({ workingDay }) => workingDay)
            .includes(true)

        return {
          configuration: flattenedWeekendDeliveryConfigurations,
          isWeekendDeliveryConfigured,
          totalAvailableMarkets,
        }
      },
    },
  )
}
